import API from '../config/api';
import React, { useEffect, useState } from 'react';

import axios from 'axios';
import { toast } from 'react-toastify';

import { Container, Card, Button, Modal, Form } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';

import CustomNavbar from '../components/CustomNavbar';

import { downloadInvoice } from '../utils/orderInvoice';

import {
  OrderTimeline,
  PLACEHOLDER_IMG,
  canCancel,
  countUnits,
  formatDate,
  getDaysToDeliver,
  getExpectedDate,
  getProgress,
  metaFor,
  orderCommonStyles
} from '../components/orders/orderCommon';

/* The full picture of a single order. The orders list only carries an
   overview; everything heavy — timeline, addresses, per-item pricing,
   payment breakdown and the cancel flow — lives here. */
function OrderDetailsPage() {
  const { orderId } = useParams();

  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [refunded, setRefunded] = useState(false);
  const [loading, setLoading] = useState(true);

  /* Cancellation — only possible before the order is out for delivery */
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelDescription, setCancelDescription] = useState('');
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    fetchOrder();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [orderId]);

  /* There is no single-order endpoint for customers, so we pull the user's
     orders (the same call the list page makes) and pick this one out. */
  const fetchOrder = async () => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));

      if (!userInfo) {
        setLoading(false);
        return;
      }

      const [ordersRes, refundsRes] = await Promise.all([
        axios.get(`${API}/orders/${userInfo._id}`),
        axios.get(`${API}/refunds/user/${userInfo._id}`)
      ]);

      const found = ordersRes.data.find(
        (o) => o._id?.toString() === orderId?.toString()
      );

      setOrder(found || null);

      setRefunded(
        refundsRes.data.some(
          (refund) => refund.order?.toString() === orderId?.toString()
        )
      );
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const closeCancelModal = () => {
    setShowCancel(false);
    setCancelReason('');
    setCancelDescription('');
  };

  const submitCancel = async () => {
    if (!cancelReason) {
      toast.error('Please select a reason for cancelling');
      return;
    }

    if (cancelReason === 'Other' && !cancelDescription.trim()) {
      toast.error('Please tell us a little more about the reason');
      return;
    }

    try {
      setCancelling(true);

      await axios.put(
        `${API}/orders/${order._id}/cancel`,

        {
          reason: cancelReason,

          description: cancelDescription
        }
      );

      toast.success('Order Cancelled Successfully');

      closeCancelModal();

      /* Refetch rather than patching locally — the list response also carries
         each item's product image, which the cancel response doesn't. */
      fetchOrder();
    } catch (error) {
      console.log(error);

      toast.error(
        error.response?.data?.message || 'Failed to cancel this order'
      );
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <>
        <CustomNavbar />

        <div className="orders-wrap">
          <Container>
            <div className="orders-skel od-skel-head mb-4" />
            <div className="orders-skel od-skel-body" />
          </Container>
        </div>

        <style>{orderCommonStyles + orderDetailsStyles}</style>
      </>
    );
  }

  if (!order) {
    return (
      <>
        <CustomNavbar />

        <div className="orders-wrap">
          <Container>
            <Card className="p-5 text-center shadow-sm border-0 od-missing">
              <div className="od-missing-icon">🔍</div>

              <h3 className="fw-bold mt-3">Order not found</h3>

              <p className="text-muted mb-4">
                We couldn't find this order on your account. It may have been
                placed with a different login.
              </p>

              <div>
                <Link to="/orders">
                  <Button className="od-cta fw-semibold px-4 py-2">
                    ← Back to My Orders
                  </Button>
                </Link>
              </div>
            </Card>
          </Container>
        </div>

        <style>{orderCommonStyles + orderDetailsStyles}</style>
      </>
    );
  }

  const meta = metaFor(order.status);

  const units = countUnits(order);

  const subtotal = (order.orderItems || []).reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const discount = order.discountAmount || 0;

  /* Timestamps we actually have, oldest first — the order's own history. */
  const journey = [
    { label: 'Order placed', at: order.createdAt, icon: '🧾' },

    order.status === 'Delivered' && {
      label: 'Delivered',
      at: order.deliveredAt || order.updatedAt,
      icon: '✅'
    },

    order.status === 'Cancelled' && {
      label: 'Cancelled',
      at: order.cancelledAt || order.updatedAt,
      icon: '✖'
    }
  ].filter(Boolean);

  return (
    <>
      <CustomNavbar />

      <div className="orders-wrap">
        <Container>
          <Link to="/orders" className="od-back mb-3">
            <span className="od-back-arrow">←</span> Back to My Orders
          </Link>

          <Card
            className="border-0 order-card od-card"
            style={{ '--o-accent': meta.accent, '--o-soft': meta.soft }}
          >
            <span className="order-card-strip" />

            {/* Header — identity on the left, state + money on the right */}
            <div className="order-head">
              <div className="order-head-id">
                <span className="order-avatar">{meta.icon}</span>

                <div className="order-id-text">
                  <h5 className="order-id mb-0">
                    Order #{order._id.slice(-6)}
                  </h5>

                  <div className="order-sub">
                    Placed on {formatDate(order.createdAt)}
                    {' · '}
                    {units} item{units === 1 ? '' : 's'}
                  </div>
                </div>
              </div>

              <div className="order-head-meta">
                <span className="order-status-pill">
                  <span className="order-status-dot" />
                  {order.status}
                </span>

                <div className="order-total">
                  <span className="order-total-label">Total</span>
                  <span className="order-total-value">₹{order.total}</span>
                </div>
              </div>
            </div>

            <Card.Body className="p-4">
              {/* ===== Where the order is right now ===== */}
              <section className="od-section">
                <div className="order-label">📍 Delivery Status</div>

                {order.status === 'Cancelled' ? (
                  <div className="order-cancel-banner">
                    <span className="order-cancel-icon">✖</span>

                    <div>
                      <div className="fw-bold">This order was cancelled</div>

                      {order.cancelReason && (
                        <small>Reason: {order.cancelReason}</small>
                      )}

                      {order.cancelDescription && (
                        <div>
                          <small className="text-muted">
                            {order.cancelDescription}
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <OrderTimeline status={order.status} />

                    <div className="od-progress-wrap">
                      <div className="order-progress">
                        <span
                          className="order-progress-fill"
                          style={{ width: `${getProgress(order.status)}%` }}
                        />
                      </div>

                      <small className="order-progress-label">
                        {getProgress(order.status)}% of the way to your door
                        {order.status !== 'Delivered' && (
                          <>
                            {' · arriving around '}
                            {getExpectedDate(order.status)} (
                            {getDaysToDeliver(order.status)} day
                            {getDaysToDeliver(order.status) > 1 ? 's' : ''})
                          </>
                        )}
                      </small>
                    </div>
                  </>
                )}
              </section>

              {/* ===== Key facts ===== */}
              <section className="od-section">
                <div className="order-label">🗂 Order Information</div>

                <div className="order-info-grid">
                  <div className="order-info">
                    <div className="order-label">
                      {order.status === 'Cancelled'
                        ? '❌ Cancelled On'
                        : order.status === 'Delivered'
                          ? '✅ Delivered On'
                          : '🚚 Expected Delivery'}
                    </div>

                    {order.status === 'Cancelled' ? (
                      <div className="order-info-value">
                        {new Date(
                          order.cancelledAt || order.updatedAt
                        ).toLocaleDateString()}
                      </div>
                    ) : order.status === 'Delivered' ? (
                      <div className="order-info-value">
                        {new Date(
                          order.deliveredAt || order.updatedAt
                        ).toLocaleDateString()}
                      </div>
                    ) : (
                      <>
                        <div className="order-info-value">
                          {getExpectedDate(order.status)}
                        </div>

                        <small className="text-muted">
                          in {getDaysToDeliver(order.status)} day
                          {getDaysToDeliver(order.status) > 1 ? 's' : ''}
                        </small>
                      </>
                    )}
                  </div>

                  <div className="order-info">
                    <div className="order-label">📍 Delivery Address</div>

                    <div className="order-info-value">{order.address}</div>

                    <small className="text-muted">
                      {order.city} - {order.pincode}
                    </small>
                  </div>

                  <div className="order-info">
                    <div className="order-label">👤 Contact</div>

                    <div className="order-info-value">{order.customerName}</div>

                    <small className="text-muted">📞 {order.phone}</small>
                  </div>

                  <div className="order-info">
                    <div className="order-label">💳 Payment</div>

                    {order.paymentId ? (
                      <>
                        <div className="order-info-value">Paid Online</div>

                        <small className="text-muted order-pay-id">
                          {order.paymentId}
                        </small>
                      </>
                    ) : (
                      <>
                        <div className="order-info-value">Cash On Delivery</div>

                        <small className="text-muted">
                          {order.codCollected
                            ? 'Payment collected'
                            : 'Pay when it arrives'}
                        </small>
                      </>
                    )}
                  </div>
                </div>
              </section>

              {/* ===== Items + money ===== */}
              <section className="od-section">
                <div className="order-label">
                  🛍 Products ({order.orderItems?.length || 0})
                </div>

                <div className="od-split">
                  <div className="order-products-grid od-items">
                    {(order.orderItems || []).map((item, index) => (
                      <Link
                        key={index}
                        to={`/product/${item.productId}`}
                        className="order-product"
                      >
                        <img
                          src={item.image || PLACEHOLDER_IMG}
                          alt={item.name}
                          className="order-product-img"
                          onError={(e) => {
                            e.currentTarget.src = PLACEHOLDER_IMG;
                          }}
                        />

                        <div className="order-product-info">
                          <span className="order-product-name">
                            {item.name}
                          </span>

                          <span className="order-product-qty">
                            Qty: {item.quantity} · ₹{item.price} each
                          </span>
                        </div>

                        <div className="order-product-price">
                          ₹{item.price * item.quantity}
                        </div>

                        <span className="order-product-chevron">›</span>
                      </Link>
                    ))}
                  </div>

                  <div className="od-bill">
                    <div className="order-label mb-2">🧾 Bill Summary</div>

                    <div className="od-bill-row">
                      <span>Subtotal</span>
                      <span>₹{subtotal}</span>
                    </div>

                    {discount > 0 && (
                      <div className="od-bill-row is-save">
                        <span>
                          Discount
                          {order.couponCode ? ` (${order.couponCode})` : ''}
                        </span>
                        <span>- ₹{discount}</span>
                      </div>
                    )}

                    <div className="od-bill-row">
                      <span>Delivery</span>
                      <span className="is-save">Free</span>
                    </div>

                    <div className="od-bill-total">
                      <span>Total Paid</span>
                      <span>₹{order.total}</span>
                    </div>

                    <div className="od-bill-note">
                      {order.paymentId
                        ? 'Paid online · ' + order.paymentId
                        : 'Cash on delivery'}
                    </div>
                  </div>
                </div>
              </section>

              {/* ===== History ===== */}
              <section className="od-section">
                <div className="order-label">🕒 Order History</div>

                <div className="od-journey">
                  {journey.map((step) => (
                    <div className="od-journey-row" key={step.label}>
                      <span className="od-journey-icon">{step.icon}</span>

                      <span className="od-journey-label">{step.label}</span>

                      <span className="od-journey-date">
                        {new Date(step.at).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </section>

              {/* ===== Actions ===== */}
              <div className="od-actions order-actions">
                <Button
                  variant={
                    order.status === 'Delivered'
                      ? 'outline-success'
                      : 'outline-secondary'
                  }
                  disabled={order.status !== 'Delivered'}
                  onClick={() => downloadInvoice(order)}
                  className="fw-semibold"
                  title={
                    order.status === 'Delivered'
                      ? 'Download the invoice PDF'
                      : 'The invoice is available once the order is delivered'
                  }
                >
                  📄 Download Invoice
                </Button>

                {order.status === 'Delivered' && (
                  <Button
                    variant={refunded ? 'secondary' : 'warning'}
                    className="fw-semibold"
                    disabled={refunded}
                    onClick={() => navigate(`/apply-refund/${order._id}`)}
                  >
                    {refunded ? '✓ Refund Requested' : '↩ Request Refund'}
                  </Button>
                )}

                {canCancel(order.status) && (
                  <Button
                    variant="outline-danger"
                    className="fw-semibold"
                    onClick={() => setShowCancel(true)}
                  >
                    ✖ Cancel Order
                  </Button>
                )}

                {order.status === 'Out For Delivery' && (
                  <Button
                    variant="outline-secondary"
                    className="fw-semibold"
                    title="Out for delivery — this order can no longer be cancelled"
                    disabled
                  >
                    ✖ Cancel Order
                  </Button>
                )}

                <Link to="/help">
                  <Button variant="outline-dark" className="fw-semibold">
                    💬 Need Help
                  </Button>
                </Link>
              </div>
            </Card.Body>
          </Card>
        </Container>
      </div>

      {/* Cancel order — reason form */}
      <Modal show={showCancel} onHide={closeCancelModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Cancel Order</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="cancel-order-summary mb-3">
            <div className="fw-bold">Order #{order._id.slice(-6)}</div>

            <div className="text-muted small">
              {order.orderItems
                ?.map((item) => `${item.name} × ${item.quantity}`)
                .join(', ')}
            </div>

            <div className="text-muted small">Total: ₹{order.total}</div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>
              Reason <span className="text-danger">*</span>
            </Form.Label>

            <Form.Select
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            >
              <option value="">Select Reason</option>

              <option>Ordered by mistake</option>

              <option>Found a better price elsewhere</option>

              <option>Delivery is taking too long</option>

              <option>Changed my mind</option>

              <option>Wrong address or contact details</option>

              <option>Other</option>
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Label>
              Description{' '}
              <span className="text-muted fw-normal">
                {cancelReason === 'Other' ? '(required)' : '(optional)'}
              </span>
            </Form.Label>

            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Tell us a bit more so we can improve…"
              value={cancelDescription}
              onChange={(e) => setCancelDescription(e.target.value)}
            />
          </Form.Group>

          <div className="text-muted small mt-3">
            ⚠ Cancelling is permanent. If you paid online, the amount is
            refunded to your original payment method within 5–7 working days.
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeCancelModal}
            disabled={cancelling}
          >
            Keep Order
          </Button>

          <Button variant="danger" onClick={submitCancel} disabled={cancelling}>
            {cancelling ? 'Cancelling…' : 'Confirm Cancellation'}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{orderCommonStyles + orderDetailsStyles}</style>
    </>
  );
}

const orderDetailsStyles = `
  .od-back {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-weight: 700;
    font-size: 0.88rem;
    text-decoration: none;
    color: #7c2d12;
    background: rgba(255,255,255,0.7);
    border: 1px solid #f1e4d0;
    border-radius: 30px;
    padding: 8px 18px;
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  }
  .od-back:hover {
    color: #9a3412;
    background: #fff;
    transform: translateX(-3px);
    box-shadow: 0 8px 18px rgba(124, 45, 18, 0.14);
  }
  .od-back-arrow { transition: transform 0.2s ease; }
  .od-back:hover .od-back-arrow { transform: translateX(-3px); }

  /* The detail card is the page itself — it shouldn't lift on hover */
  .od-card:hover { transform: none; }

  .od-section {
    padding-top: 20px;
    margin-top: 20px;
    border-top: 1px dashed #f1e4d0;
  }
  .od-section:first-of-type {
    padding-top: 0;
    margin-top: 0;
    border-top: none;
  }
  .od-progress-wrap { margin-top: 16px; }

  /* Items on the left, the bill on the right, stacking on narrow screens */
  .od-split {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 280px;
    gap: 16px;
    align-items: start;
  }
  .od-items { grid-template-columns: 1fr; }

  .od-bill {
    background: linear-gradient(160deg, #fffaf3, #fff);
    border: 1px solid #f1e4d0;
    border-radius: 16px;
    padding: 16px 18px;
  }
  .od-bill-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 0.88rem;
    color: #4b5563;
    padding: 6px 0;
  }
  .od-bill-row .is-save,
  .od-bill-row.is-save { color: #16a34a; font-weight: 600; }
  .od-bill-total {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-top: 10px;
    padding-top: 12px;
    border-top: 1px dashed #ead9c0;
    font-weight: 800;
    font-size: 1.05rem;
    color: #0f172a;
  }
  .od-bill-note {
    margin-top: 10px;
    font-size: 0.7rem;
    color: #a1a1aa;
    word-break: break-all;
  }

  .od-journey {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .od-journey-row {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #fbfaf8;
    border: 1px solid #f1e4d0;
    border-radius: 12px;
    padding: 10px 14px;
  }
  .od-journey-icon { font-size: 1rem; }
  .od-journey-label {
    font-weight: 700;
    font-size: 0.88rem;
    color: #1f2937;
    flex: 1 1 auto;
  }
  .od-journey-date {
    font-size: 0.78rem;
    color: #94a3b8;
    font-weight: 600;
  }

  .od-actions {
    justify-content: flex-start;
    margin-top: 24px;
    padding-top: 18px;
    border-top: 1px dashed #f1e4d0;
  }

  .od-missing {
    border-radius: 22px;
    background: linear-gradient(180deg, #ffffff, #fffaf3);
  }
  .od-missing-icon { font-size: 3.4rem; }
  .od-cta {
    border: none;
    border-radius: 30px;
    background: linear-gradient(135deg, #0f172a, #7c2d12);
  }
  .od-cta:hover { background: linear-gradient(135deg, #1e293b, #9a3412); }

  .od-skel-head { height: 96px; }
  .od-skel-body { height: 520px; }

  @media (max-width: 991.98px) {
    .od-split { grid-template-columns: 1fr; }
  }
  @media (max-width: 767.98px) {
    .od-actions .btn { flex: 1 1 auto; }
  }
`;

export default OrderDetailsPage;
